import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { ErrorBox, SkeletonRows, StatusPill } from "../components/States";
import { useBackButton } from "../components/telegram";
import { fmtMoney } from "../format";
import { MeResponse } from "../types";

interface OverviewDeal {
  id: number;
  deal_type: string;
  amount: number;
  currency: string;
  status: string;
  deal_date: string | null;
  company: string | null;
}
interface OverviewWallet {
  id: number;
  address: string;
  name: string;
  network: string;
  is_active: boolean;
}
interface Overview {
  id: number;
  name: string;
  kind: string;
  status: string;
  created_at: string | null;
  deal_count: number;
  open_count: number;
  totals_by_currency: Record<string, number>;
  deals: OverviewDeal[];
  pnl: {
    deal_count?: number;
    total_expected?: number;
    total_cost?: number;
    total_pnl?: number;
    total_pnl_pct?: number | null;
  };
  wallets: OverviewWallet[];
}

function dealTone(
  status: string,
): "neutral" | "info" | "success" | "warning" | "danger" {
  if (status === "confirmed") return "success";
  if (status === "cancelled") return "danger";
  return "warning";
}

interface DebtSummary {
  total_debit: number;
  total_credit: number;
  deals: Array<{
    deal_id: number;
    expected: number;
    received: number;
    debit: number;
    credit: number;
  }>;
}

/** Карточка контрагента 360°: профиль, сделки, P&L, кошельки. */
export function CounterpartyCard({
  cpId,
  me,
  onClose,
}: {
  cpId: number;
  me: MeResponse;
  onClose: () => void;
}) {
  const [data, setData] = useState<Overview | null>(null);
  const [debt, setDebt] = useState<DebtSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useBackButton(onClose, true);

  const loadDebt = () => {
    api
      .get<DebtSummary>(`/api/rub_inflows/counterparty/${cpId}`)
      .then(setDebt)
      .catch(() => {
        /* дебиторка не критична для карточки */
      });
  };

  useEffect(() => {
    setData(null);
    setDebt(null);
    setError(null);
    api
      .get<Overview>(`/api/counterparties/${cpId}/overview`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    loadDebt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpId]);

  const canOffset =
    me.permissions.payments_confirm || me.permissions.payments_write;

  return (
    <div className="space-y-3">
      <button onClick={onClose} className="text-tg-link text-sm">
        ‹ Назад
      </button>

      {error && <ErrorBox text={error} />}
      {!data && !error && <SkeletonRows rows={5} />}

      {data && (
        <>
          <div>
            <h1 className="text-xl font-semibold">{data.name}</h1>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <StatusPill tone="neutral">{data.kind}</StatusPill>
              <StatusPill
                tone={data.status === "active" ? "success" : "warning"}
              >
                {data.status}
              </StatusPill>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-2xl font-bold tabular-nums">
                {data.deal_count}
              </div>
              <div className="text-xs text-tg-hint">всего сделок</div>
            </div>
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-2xl font-bold tabular-nums">
                {data.open_count}
              </div>
              <div className="text-xs text-tg-hint">в работе</div>
            </div>
          </div>

          {Object.keys(data.totals_by_currency).length > 0 && (
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
                Оборот по валютам
              </div>
              <ul className="text-sm space-y-0.5">
                {Object.entries(data.totals_by_currency)
                  .sort()
                  .map(([c, v]) => (
                    <li key={c} className="flex justify-between">
                      <span className="text-tg-hint">{c}</span>
                      <span className="font-medium tabular-nums">
                        {fmtMoney(v)}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {data.pnl && data.pnl.deal_count != null && (
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
                P&L (по проведённым оплатам)
              </div>
              {data.pnl.deal_count === 0 ? (
                <div className="text-sm text-tg-hint">
                  Проведённых оплат пока нет.
                </div>
              ) : (
                <dl className="text-sm grid grid-cols-[1fr_auto] gap-x-3 gap-y-1">
                  <dt className="text-tg-hint">Сделок в P&L</dt>
                  <dd className="text-right">{data.pnl.deal_count}</dd>
                  <dt className="text-tg-hint">Ожидаемый приход</dt>
                  <dd className="text-right tabular-nums">
                    {fmtMoney(data.pnl.total_expected || 0)} ₽
                  </dd>
                  <dt className="text-tg-hint">Себестоимость</dt>
                  <dd className="text-right tabular-nums">
                    {fmtMoney(data.pnl.total_cost || 0)} ₽
                  </dd>
                  <dt className="text-tg-hint font-medium">P&L</dt>
                  <dd className="text-right tabular-nums font-semibold">
                    {fmtMoney(data.pnl.total_pnl || 0)} ₽
                    {data.pnl.total_pnl_pct != null
                      ? ` (${data.pnl.total_pnl_pct.toFixed(2)}%)`
                      : ""}
                  </dd>
                </dl>
              )}
            </div>
          )}

          {debt && (debt.total_debit > 0 || debt.total_credit > 0) && (
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
                Расчёты по рублям
              </div>
              <div className="flex gap-2 mb-1">
                {debt.total_debit > 0 && (
                  <StatusPill tone="danger">
                    дебиторка {fmtMoney(debt.total_debit)} ₽
                  </StatusPill>
                )}
                {debt.total_credit > 0 && (
                  <StatusPill tone="warning">
                    кредиторка {fmtMoney(debt.total_credit)} ₽
                  </StatusPill>
                )}
              </div>
              <ul className="text-xs space-y-0.5">
                {debt.deals.map((d) => (
                  <li
                    key={d.deal_id}
                    className="flex justify-between gap-2"
                  >
                    <span className="text-tg-hint">№{d.deal_id}</span>
                    <span className="tabular-nums">
                      {fmtMoney(d.received)} / {fmtMoney(d.expected)} ₽
                      {d.debit > 0 && (
                        <span style={{ color: "#dc2626" }}>
                          {" "}
                          −{fmtMoney(d.debit)}
                        </span>
                      )}
                      {d.credit > 0 && (
                        <span style={{ color: "#d97706" }}>
                          {" "}
                          +{fmtMoney(d.credit)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {canOffset &&
                debt.total_debit > 0 &&
                debt.total_credit > 0 && (
                  <OffsetForm debt={debt} onDone={loadDebt} />
                )}
            </div>
          )}

          {data.wallets.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
                Кошельки
              </div>
              <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
                {data.wallets.map((w) => (
                  <div key={w.id} className="px-4 py-2.5 text-sm">
                    {w.name && <div className="font-medium">{w.name}</div>}
                    <div className="font-mono text-xs break-all text-tg-hint">
                      {w.address}
                    </div>
                    <div className="text-[11px] text-tg-hint mt-0.5">
                      {w.network}
                      {!w.is_active ? " · неактивен" : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
              История сделок
            </div>
            {data.deals.length === 0 ? (
              <div className="text-sm text-tg-hint py-4 text-center">
                Сделок нет.
              </div>
            ) : (
              <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
                {data.deals.map((d) => (
                  <div key={d.id} className="px-4 py-2.5 text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium">
                        №{d.id} · {d.deal_type}
                      </span>
                      <StatusPill tone={dealTone(d.status)}>
                        {d.status}
                      </StatusPill>
                    </div>
                    <div className="mt-0.5">
                      <span className="font-semibold tabular-nums">
                        {fmtMoney(d.amount)} {d.currency}
                      </span>
                      <span className="text-tg-hint text-xs">
                        {d.company ? ` · ${d.company}` : ""}
                        {d.deal_date ? ` · ${d.deal_date}` : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Зачёт переплаты по одной заявке в счёт дебиторки по другой. */
function OffsetForm({
  debt,
  onDone,
}: {
  debt: DebtSummary;
  onDone: () => void;
}) {
  const creditDeals = debt.deals.filter((d) => d.credit > 0);
  const debitDeals = debt.deals.filter((d) => d.debit > 0);

  const [open, setOpen] = useState(false);
  const [fromId, setFromId] = useState(creditDeals[0]?.deal_id ?? 0);
  const [toId, setToId] = useState(debitDeals[0]?.deal_id ?? 0);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromDeal = creditDeals.find((d) => d.deal_id === fromId);
  const toDeal = debitDeals.find((d) => d.deal_id === toId);
  const maxAmount = Math.min(
    fromDeal?.credit ?? 0,
    toDeal?.debit ?? 0,
  );

  useEffect(() => {
    setAmount(maxAmount > 0 ? String(maxAmount) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromId, toId]);

  const submit = async () => {
    const v = Number(amount.replace(",", ".").replace(/\s/g, ""));
    if (!(v > 0)) {
      setError("Введите сумму зачёта больше нуля.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/rub_inflows/offset", {
        from_deal_id: fromId,
        to_deal_id: toId,
        amount: v,
      });
      setOpen(false);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 w-full py-1.5 rounded-lg bg-tg-secondaryBg text-sm"
      >
        ⇄ Зачесть переплату
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 text-xs">
      <label className="block">
        <span className="text-tg-hint">Переплата по заявке</span>
        <select
          value={fromId}
          onChange={(e) => setFromId(Number(e.target.value))}
          className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text"
        >
          {creditDeals.map((d) => (
            <option key={d.deal_id} value={d.deal_id}>
              №{d.deal_id} · переплата {fmtMoney(d.credit)} ₽
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-tg-hint">В счёт дебиторки по заявке</span>
        <select
          value={toId}
          onChange={(e) => setToId(Number(e.target.value))}
          className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text"
        >
          {debitDeals.map((d) => (
            <option key={d.deal_id} value={d.deal_id}>
              №{d.deal_id} · остаток {fmtMoney(d.debit)} ₽
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-2 items-center">
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder={`Сумма ₽ (макс. ${fmtMoney(maxAmount)})`}
          className="flex-1 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text"
        />
        <button
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText disabled:opacity-50"
        >
          Зачесть
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-hint"
        >
          ✕
        </button>
      </div>
      {error && <ErrorBox text={error} />}
    </div>
  );
}
